import { LineChart } from "@mantine/charts";
import {
  Badge,
  Box,
  Card,
  Grid,
  Group,
  rem,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Printable, usePrintableContext } from "@components";
import {
  OcularMotilityData,
  SmoothPursuitDirectionMetrics,
  SmoothPursuitMetrics,
  SmoothPursuitTestResult,
} from "types/testTypes";
import { snapshotsToChartData } from "types/tests/smoothPursuitUtils";
import CircularPursuitGraph, {
  computeGazeExtent,
  GazeExtent,
  mergeGazeExtents,
} from "../../common/CircularPursuitGraph";
import {
  assessSmoothPursuit,
  getSmoothPursuitDirectionMetrics,
  getSmoothPursuitErrorAxis,
  getSmoothPursuitGainValues,
  getSmoothPursuitValidity,
  normalizeSmoothPursuitChartData,
  SMOOTH_PURSUIT_DIRECTIONS,
  SmoothPursuitDirection,
  SmoothPursuitQualityLabel,
  SmoothPursuitRiskLabel,
  splitSmoothPursuitChartData,
} from "../utils/smoothPursuitResult";

const TR = "SmoothPursuitResult";

const RISK_COLOR_MAP: Record<SmoothPursuitRiskLabel, string> = {
  normal: "green",
  borderline: "yellow",
  review: "red",
  // Grey rather than red: nothing was measured, so nothing is being claimed
  // about the patient or the headset - the test has to be repeated.
  insufficient: "gray",
};

const QUALITY_COLOR_MAP: Record<SmoothPursuitQualityLabel, string> = {
  smooth: "green",
  elevated: "yellow",
  saccadic: "orange",
};

interface SmoothPursuitTestEntryProps {
  entry: SmoothPursuitTestResult | null;
  header?: ReactNode;
  /** Rendered on the second page, where there is room left for it. */
  printGroup?: string;
}

const directionLabelKey: Record<SmoothPursuitDirection, string> = {
  clockwise: `${TR}.directions.clockwise`,
  counterClockwise: `${TR}.directions.counterClockwise`,
};

function formatNumber(value: unknown, decimals = 2, fallback = "-") {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(decimals) : fallback;
}

function formatPercent(value: unknown, fallback = "-") {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(1) : fallback;
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Box w={14} h={7} bg={color} />
      <Text fz="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}

function SmoothPursuitLegend() {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Group gap="lg" wrap="wrap">
      <LegendSwatch color={theme.colors.red[6]} label={t(`${TR}.legend.target`)} />
      <LegendSwatch color={theme.colors.blue[6]} label={t(`${TR}.legend.leftGaze`)} />
      <LegendSwatch color={theme.colors.green[6]} label={t(`${TR}.legend.rightGaze`)} />
    </Group>
  );
}

function SmoothPursuitConfigCard({
  entry,
  metrics,
}: {
  entry: SmoothPursuitTestResult | null;
  metrics: SmoothPursuitMetrics | null;
}) {
  const { t } = useTranslation();
  const snapshots = entry?.results?.snapshots ?? [];
  const testConfig = entry?.results?.testConfig;
  const targetVelocity = metrics?.targetVelocity ?? testConfig?.angularSpeed ?? null;
  const validity = getSmoothPursuitValidity(snapshots, metrics);
  const noData = t(`${TR}.noData`);

  return (
    <Card withBorder radius="md" p="sm">
      <Group gap="xl" wrap="wrap">
        <Text fz="xs">
          <b>{t(`${TR}.config.targetVelocity`)}</b>{" "}
          {targetVelocity != null
            ? t(`${TR}.config.targetVelocityValue`, { value: formatNumber(targetVelocity, 1) })
            : noData}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.config.validSamples`)}</b>{" "}
          {validity.left != null && validity.right != null
            ? t(`${TR}.config.validSamplesValue`, {
                left: formatPercent(validity.left),
                right: formatPercent(validity.right),
              })
            : noData}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.config.device`)}</b> {entry?.device ?? noData}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.config.build`)}</b> {entry?.version ?? noData}
        </Text>
      </Group>
    </Card>
  );
}

function SmoothPursuitResultCard({ metrics }: { metrics: SmoothPursuitMetrics | null }) {
  const { t } = useTranslation();
  const assessment = assessSmoothPursuit(metrics);
  const riskLabel = assessment?.risk ?? null;

  if (!riskLabel) {
    return (
      <Card withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fz="xs" fw={800} tt="uppercase" c="dimmed" lts="0.2em">
            {t(`${TR}.result.eyebrow`)}
          </Text>
          <Title order={3} lh={1.1}>
            {t(`${TR}.noData`)}
          </Title>
        </Stack>
      </Card>
    );
  }

  const riskColor = RISK_COLOR_MAP[riskLabel];
  const gainValues = getSmoothPursuitGainValues(metrics);
  const quality = assessment?.quality ?? null;

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      bg={`${riskColor}.0`}
      style={{
        borderColor: `var(--mantine-color-${riskColor}-3)`,
        borderLeft: `${rem(8)} solid var(--mantine-color-${riskColor}-6)`,
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Text fz="xs" fw={800} tt="uppercase" c={`${riskColor}.7`} lts="0.2em">
              {t(`${TR}.result.eyebrow`)}
            </Text>
            <Title order={3} c={`${riskColor}.9`} lh={1.1}>
              {t(`${TR}.result.headline.${riskLabel}`)}
            </Title>
          </Stack>
          <Badge color={riskColor} variant="filled" radius="sm">
            {t(`${TR}.risk.${riskLabel}`)}
          </Badge>
        </Group>

        <Text size="sm" fw={500} c={`${riskColor}.9`} lh={1.5}>
          {t(`${TR}.result.description.${riskLabel}`)}
        </Text>

        <Stack gap={4}>
          <Text fz="10px" c="dimmed" tt="uppercase" fw={700} lts="0.1em">
            {t(`${TR}.result.gainLabel`)}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {gainValues.map((item) => (
              <Stack key={`${item.direction}-${item.eye}`} gap={2}>
                <Text fz="10px" c="dimmed" tt="uppercase" fw={700} lh={1.2}>
                  {t(directionLabelKey[item.direction])} /{" "}
                  {t(item.eye === "left" ? `${TR}.eyes.left` : `${TR}.eyes.right`)}
                </Text>
                <Text fz="lg" fw={700} lh={1.1}>
                  {formatNumber(item.value, 2, t(`${TR}.noData`))}
                </Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>

        {quality && (
          <Group gap="xs" wrap="nowrap" align="center">
            <Text fz="xs" fw={700} c="dimmed" tt="uppercase" lts="0.1em">
              {t(`${TR}.result.quality.title`)}
            </Text>
            <Badge color={QUALITY_COLOR_MAP[quality]} variant="light" radius="sm">
              {t(`${TR}.result.quality.${quality}`)}
            </Badge>
            <Text fz="xs" c="dimmed" lh={1.4}>
              {t(
                assessment?.escalatedByQuality
                  ? `${TR}.result.quality.escalatedNote`
                  : `${TR}.result.quality.${quality}Note`,
              )}
            </Text>
          </Group>
        )}

        {assessment?.amplitudeImplausible && (
          <Group gap="xs" wrap="nowrap" align="center">
            <Text fz="xs" fw={700} c="dimmed" tt="uppercase" lts="0.1em">
              {t(`${TR}.result.amplitude.title`)}
            </Text>
            <Badge color="orange" variant="light" radius="sm">
              {t(`${TR}.result.amplitude.implausible`)}
            </Badge>
            <Text fz="xs" c="dimmed" lh={1.4}>
              {t(`${TR}.result.amplitude.implausibleNote`)}
            </Text>
          </Group>
        )}

        <Text fz="xs" c={`${riskColor}.9`} lh={1.4}>
          {t(`${TR}.result.referenceNote`)}
        </Text>
      </Stack>
    </Card>
  );
}

function PhaseGraphSection({
  direction,
  data,
  sharedExtent,
}: {
  direction: SmoothPursuitDirection;
  data: OcularMotilityData[];
  sharedExtent: GazeExtent;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="xs">
      <Text fz="xs" fw={700} tt="uppercase" c="dimmed">
        {t(directionLabelKey[direction])}
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Box maw={338} mx="auto" w="100%">
          <CircularPursuitGraph
            data={data}
            visibleEye="left"
            title={t(`${TR}.eyes.left`)}
            compact
            height={338}
            showPaper={false}
            showLegend={false}
            sharedExtent={sharedExtent}
          />
        </Box>
        <Box maw={338} mx="auto" w="100%">
          <CircularPursuitGraph
            data={data}
            visibleEye="right"
            title={t(`${TR}.eyes.right`)}
            compact
            height={338}
            showPaper={false}
            showLegend={false}
            sharedExtent={sharedExtent}
          />
        </Box>
      </SimpleGrid>
    </Stack>
  );
}

function SmoothPursuitGazePanel({
  phaseData,
}: {
  phaseData: Record<SmoothPursuitDirection, OcularMotilityData[]>;
}) {
  const { t } = useTranslation();

  const sharedExtent = useMemo(
    () =>
      mergeGazeExtents(
        SMOOTH_PURSUIT_DIRECTIONS.map((direction) => computeGazeExtent(phaseData[direction])),
      ),
    [phaseData],
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4}>
            <Title order={5}>{t(`${TR}.gazePanel.title`)}</Title>
            <Text fz="xs" c="dimmed">
              {t(`${TR}.gazePanel.helper`)}
            </Text>
          </Stack>
          <SmoothPursuitLegend />
        </Group>

        {SMOOTH_PURSUIT_DIRECTIONS.map((direction) => (
          <PhaseGraphSection
            key={direction}
            direction={direction}
            data={phaseData[direction]}
            sharedExtent={sharedExtent}
          />
        ))}
      </Stack>
    </Card>
  );
}

const METRIC_ROWS: {
  key: keyof SmoothPursuitDirectionMetrics;
  labelKey: string;
  decimals: number;
}[] = [
  // Inclusive gain first: it is the headline the result card bands on. Orbit
  // coverage and the desaccaded pursuit gain follow as mechanism detail - the
  // latter reads ~0.80 on a healthy eye by construction, so it is not a number
  // to compare against published ~1.0 gains. See smoothPursuitResult.ts.
  { key: "leftInclusiveGain", labelKey: `${TR}.metrics.leftInclusiveGain`, decimals: 2 },
  { key: "rightInclusiveGain", labelKey: `${TR}.metrics.rightInclusiveGain`, decimals: 2 },
  { key: "leftAmplitudeRatio", labelKey: `${TR}.metrics.leftAmplitudeRatio`, decimals: 2 },
  { key: "rightAmplitudeRatio", labelKey: `${TR}.metrics.rightAmplitudeRatio`, decimals: 2 },
  { key: "leftMedianError", labelKey: `${TR}.metrics.leftMedianError`, decimals: 2 },
  { key: "rightMedianError", labelKey: `${TR}.metrics.rightMedianError`, decimals: 2 },
  { key: "leftPursuitGain", labelKey: `${TR}.metrics.leftPursuitGain`, decimals: 2 },
  { key: "rightPursuitGain", labelKey: `${TR}.metrics.rightPursuitGain`, decimals: 2 },
  {
    key: "leftSaccadeMaskedFraction",
    labelKey: `${TR}.metrics.leftSaccadicShare`,
    decimals: 2,
  },
  {
    key: "rightSaccadeMaskedFraction",
    labelKey: `${TR}.metrics.rightSaccadicShare`,
    decimals: 2,
  },
  { key: "leftSaccadeCount", labelKey: `${TR}.metrics.leftSaccades`, decimals: 0 },
  { key: "rightSaccadeCount", labelKey: `${TR}.metrics.rightSaccades`, decimals: 0 },
];

function SmoothPursuitMetricsCard({ metrics }: { metrics: SmoothPursuitMetrics | null }) {
  const { t } = useTranslation();
  const noData = t(`${TR}.noData`);
  const byDirection = SMOOTH_PURSUIT_DIRECTIONS.map((direction) => ({
    direction,
    values: getSmoothPursuitDirectionMetrics(metrics, direction),
  }));

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Stack gap={4}>
          <Title order={5}>{t(`${TR}.metrics.title`)}</Title>
          <Text fz="xs" c="dimmed">
            {t(`${TR}.metrics.helper`)}
          </Text>
        </Stack>
        <Table withTableBorder withColumnBorders verticalSpacing="xs" layout="auto">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t(`${TR}.metrics.metric`)}</Table.Th>
              {byDirection.map(({ direction }) => (
                <Table.Th key={direction} ta="right">
                  {t(directionLabelKey[direction])}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {METRIC_ROWS.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>{t(row.labelKey)}</Table.Td>
                {byDirection.map(({ direction, values }) => (
                  <Table.Td key={direction} ta="right">
                    {formatNumber(values?.[row.key], row.decimals, noData)}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

function SmoothPursuitErrorCard({ data }: { data: OcularMotilityData[] }) {
  const { t } = useTranslation();
  const axis = useMemo(() => getSmoothPursuitErrorAxis(data), [data]);

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Stack gap={4}>
          <Title order={5}>{t(`${TR}.errorChart.title`)}</Title>
          <Text fz="xs" c="dimmed">
            {t(`${TR}.errorChart.helper`)}
          </Text>
        </Stack>
        <LineChart
          h={240}
          data={data}
          withDots={false}
          withLegend
          dataKey="timestamp"
          series={[
            { name: "leftEyeError", color: "indigo.6", label: t(`${TR}.eyes.left`) },
            { name: "rightEyeError", color: "teal.6", label: t(`${TR}.eyes.right`) },
          ]}
          lineChartProps={{ syncId: "smoothPursuitErrors" }}
          legendProps={{ verticalAlign: "bottom", height: 50 }}
          xAxisLabel={t(`${TR}.errorChart.xAxis`)}
          yAxisLabel={t(`${TR}.errorChart.yAxis`)}
          xAxisProps={{
            type: "number",
            domain: [0, axis.xMax],
            ticks: axis.xTicks,
            tickFormatter: (value) => `${Math.round(Number(value))}`,
            interval: 0,
          }}
          yAxisProps={{
            type: "number",
            domain: [0, axis.yMax],
            ticks: axis.yTicks,
            tickFormatter: (value) => `${Math.round(Number(value))}`,
          }}
        />
      </Stack>
    </Card>
  );
}

export default function SmoothPursuitTestEntry({
  entry,
  header,
  printGroup,
}: SmoothPursuitTestEntryProps) {
  const { t } = useTranslation();
  const { prettier } = usePrintableContext();
  const snapshots = entry?.results?.snapshots ?? [];
  const metrics = entry?.results?.metrics ?? null;
  const testConfig = entry?.results?.testConfig ?? null;

  const chartData = useMemo(
    () => normalizeSmoothPursuitChartData(snapshotsToChartData(snapshots)),
    [snapshots],
  );
  const phaseData = useMemo(
    () => splitSmoothPursuitChartData(chartData, testConfig),
    [chartData, testConfig],
  );

  // This component owns its sections, so the empty state has to supply the
  // header and a section of its own; nothing upstream wraps it any more.
  if (!snapshots.length && !metrics) {
    return (
      <Printable.Section printGroup={printGroup}>
        <Stack gap="md">
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Text size="sm" c="dimmed">
              {t(`${TR}.empty`)}
            </Text>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  // Two pages' worth of content, grouped so each fills its page: a section is
  // at least A4 tall, so letting it overflow instead breaks mid-card and leaves
  // most of the following page blank.
  return (
    <>
      <Printable.Section printGroup={printGroup}>
        <Stack gap="md">
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Stack gap="md">
              <SmoothPursuitConfigCard entry={entry} metrics={metrics} />
              <SmoothPursuitResultCard metrics={metrics} />
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, lg: 8 }} className="sp-gaze-col">
                  <SmoothPursuitGazePanel phaseData={phaseData} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 4 }} className="sp-metrics-col">
                  <SmoothPursuitMetricsCard metrics={metrics} />
                </Grid.Col>
              </Grid>
            </Stack>
          </Box>
        </Stack>
      </Printable.Section>
      <Printable.Section printGroup={printGroup}>
        <Box mx={prettier ? 0 : "xl"}>
          <SmoothPursuitErrorCard data={chartData} />
        </Box>
      </Printable.Section>
    </>
  );
}

SmoothPursuitTestEntry.selfSections = true;
